import { Component, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { animate, style, transition, trigger } from '@angular/animations';
import { ToastService, Toast } from '../../../core/services/toast.service';
import { MatIconModule } from '@angular/material/icon';

@Component({
  selector: 'hrops-toast-container',
  standalone: true,
  imports: [CommonModule, MatIconModule],
  template: `
    <div class="fixed top-5 right-5 z-[9999] flex flex-col gap-3 pointer-events-none">
      @for (toast of toastService.toasts(); track toast.id) {
        <div
          [@toastAnimation]
          class="pointer-events-auto flex items-start gap-3 min-w-[320px] max-w-[450px] p-4 rounded-lg shadow-lg border-l-4 transition-all"
          [ngClass]="getToastClasses(toast.type)"
        >
          <div class="flex-shrink-0 mt-0.5">
            <mat-icon [class]="getIconClass(toast.type)">{{ getIconName(toast.type) }}</mat-icon>
          </div>

          <div class="flex-grow">
            @if (toast.title) {
              <h4 class="font-bold text-sm mb-1 text-gray-900">{{ toast.title }}</h4>
            }
            <p class="text-sm text-gray-700 leading-relaxed">{{ toast.message }}</p>
          </div>

          <button
            (click)="toastService.remove(toast.id)"
            class="flex-shrink-0 text-gray-400 hover:text-gray-600 transition-colors"
          >
            <mat-icon class="!text-[18px] !w-[18px] !h-[18px]">close</mat-icon>
          </button>
        </div>
      }
    </div>
  `,
  styles: [`
    :host { display: block; }
  `],
  animations: [
    trigger('toastAnimation', [
      transition(':enter', [
        style({ transform: 'translateX(100%)', opacity: 0 }),
        animate('300ms cubic-bezier(0.23, 1, 0.32, 1)', style({ transform: 'translateX(0)', opacity: 1 }))
      ]),
      transition(':leave', [
        animate('200ms ease-in', style({ transform: 'translateX(100%)', opacity: 0 }))
      ])
    ])
  ]
})
export class ToastContainerComponent {
  readonly toastService = inject(ToastService);

  getToastClasses(type: string): string {
    switch (type) {
      case 'success': return 'bg-green-50 border-green-500 shadow-green-100/50';
      case 'error':   return 'bg-red-50 border-red-500 shadow-red-100/50';
      case 'warning': return 'bg-amber-50 border-amber-500 shadow-amber-100/50';
      case 'info':    return 'bg-blue-50 border-blue-500 shadow-blue-100/50';
      default:        return 'bg-white border-gray-500';
    }
  }

  getIconClass(type: string): string {
    switch (type) {
      case 'success': return 'text-green-600';
      case 'error':   return 'text-red-600';
      case 'warning': return 'text-amber-600';
      case 'info':    return 'text-blue-600';
      default:        return 'text-gray-600';
    }
  }

  getIconName(type: string): string {
    switch (type) {
      case 'success': return 'check_circle';
      case 'error':   return 'error';
      case 'warning': return 'warning';
      case 'info':    return 'info';
      default:        return 'notifications';
    }
  }
}
